import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import { comics, categories } from '@/mocks/comics';

const detailCoverImages: Record<string, string> = {
  '1': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20cover%20art%20style%2C%20beautiful%20female%20character%20portrait%2C%20soft%20lighting%2C%20clean%20composition%2C%20warm%20toned%20background%2C%20digital%20illustration%2C%20romantic%20and%20elegant%20aesthetic&width=800&height=1140&seq=detail-cover-secret-romance&orientation=portrait',
  '2': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20fantasy%20cover%20art%2C%20young%20male%20character%20in%20magic%20academy%20uniform%2C%20glowing%20magic%20effects%2C%20dark%20blue%20and%20gold%20color%20scheme%2C%20digital%20illustration%2C%20anime%20inspired%20style&width=800&height=1140&seq=detail-cover-magic-academy&orientation=portrait',
  '3': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20action%20cover%2C%20male%20hunter%20character%20with%20dark%20outfit%2C%20shadowy%20urban%20background%2C%20dramatic%20lighting%2C%20cool%20tone%20color%20palette%2C%20digital%20art%2C%20webtoon%20style&width=800&height=1140&seq=detail-cover-shadow-hunter&orientation=portrait',
  '4': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20drama%20cover%2C%20young%20female%20character%20in%20winter%20setting%2C%20snowy%20landscape%20background%2C%20soft%20emotional%20expression%2C%20cool%20blue%20and%20white%20color%20scheme%2C%20watercolor%20inspired%20art&width=800&height=1140&seq=detail-cover-winter-tears&orientation=portrait',
  '5': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comedy%20cover%2C%20office%20workers%20in%20funny%20situation%2C%20bright%20colorful%20palette%2C%20cartoonish%20expression%2C%20clean%20modern%20office%20background%2C%20digital%20illustration&width=800&height=1140&seq=detail-cover-office-laugh&orientation=portrait',
  '6': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20thriller%20cover%2C%20detective%20character%20in%20dark%20alley%2C%20neon%20signs%20glowing%2C%20rainy%20night%20atmosphere%2C%20high%20contrast%20lighting%2C%20dark%20noir%20style&width=800&height=1140&seq=detail-cover-murder-hongdae&orientation=portrait',
  '7': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20dance%20cover%2C%20female%20dancer%20in%20elegant%20pose%2C%20stage%20spotlight%20background%2C%20flowing%20fabric%20movement%2C%20warm%20golden%20lighting%2C%20graceful%20composition&width=800&height=1140&seq=detail-cover-starlight-dancer&orientation=portrait',
  '8': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20fantasy%20cover%2C%20demon%20king%20character%20in%20school%20uniform%2C%20dark%20aura%20surrounding%2C%20modern%20school%20background%2C%20dramatic%20composition%2C%20webtoon%20style&width=800&height=1140&seq=detail-cover-demon-king&orientation=portrait',
  '9': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20romance%20cover%2C%20couple%20on%20jeju%20island%20beach%2C%20ocean%20and%20green%20fields%20background%2C%20warm%20sunset%20lighting%2C%20sweet%20atmosphere%2C%20digital%20illustration&width=800&height=1140&seq=detail-cover-love-jeju&orientation=portrait',
  '10': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20historical%20martial%20arts%20cover%2C%20swordsman%20in%20traditional%20Korean%20hanbok%2C%20ancient%20palace%20background%2C%20dramatic%20wind%20effects%2C%20ink%20brush%20artistic%20style&width=800&height=1140&seq=detail-cover-sword-destiny&orientation=portrait',
};

const relatedCoverImages = [
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20cover%20art%2C%20beautiful%20character%20illustration%2C%20soft%20pastel%20colors%2C%20clean%20background%2C%20digital%20art%20style%2C%20romantic%20mood&width=800&height=1140&seq=related-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20fantasy%20cover%2C%20magic%20character%20in%20glowing%20scene%2C%20dark%20blue%20background%2C%20ethereal%20light%2C%20digital%20illustration%2C%20anime%20style&width=800&height=1140&seq=related-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20action%20cover%2C%20warrior%20character%20dynamic%20pose%2C%20battle%20scene%2C%20warm%20dramatic%20lighting%2C%20digital%20art%2C%20powerful%20composition&width=800&height=1140&seq=related-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20slice%20of%20life%20cover%2C%20young%20people%20in%20city%2C%20warm%20sunlight%2C%20realistic%20style%2C%20digital%20illustration%2C%20modern%20aesthetic&width=800&height=1140&seq=related-04&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20mystery%20cover%2C%20character%20in%20shadows%2C%20mysterious%20atmosphere%2C%20dark%20green%20and%20black%20tones%2C%20digital%20art%2C%20suspenseful%20mood&width=800&height=1140&seq=related-05&orientation=portrait',
];

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  const comic = useMemo(
    () => comics.find((c) => c.slug === slug),
    [slug],
  );

  const category = useMemo(
    () => (comic ? categories.find((c) => c.slug === comic.category) : undefined),
    [comic],
  );

  const relatedComics = useMemo(
    () => (comic ? comics.filter((c) => c.id !== comic.id).slice(0, 5) : []),
    [comic],
  );

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到" />
        <Navbar />
        <main className="pt-16 md:pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto py-20 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">漫画未找到</h1>
              <p className="text-foreground-500 mb-6">您查找的漫画可能已下架或不存在</p>
              <Link
                to="/"
                className="inline-flex items-center px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    genre: category?.name || '',
    datePublished: comic.updateDate,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '韩漫在线',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: Math.floor(comic.views / 100),
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://wxctif.com/' },
      { '@type': 'ListItem', position: 2, name: `${category?.name || ''}漫画`, item: `http://wxctif.com/category/${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={comic.title}
        description={`${comic.title} - ${comic.description.slice(0, 120)}`}
        keywords={`${comic.title},${comic.tags.join(',')},${comic.author},韩国漫画`}
        canonicalUrl={`http://wxctif.com/comic/${comic.slug}`}
        ogType="book"
        schema={[articleSchema, breadcrumbSchema]}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <BreadcrumbNav
              items={[
                { label: '首页', href: '/' },
                { label: `${category?.name || ''}漫画`, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <section className="py-6 md:py-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                <div className="w-full md:w-[280px] lg:w-[320px] flex-shrink-0">
                  <div className="rounded-lg overflow-hidden aspect-[7/10] bg-background-100 sticky top-24">
                    <img
                      src={detailCoverImages[comic.id] || detailCoverImages['1']}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="mb-6">
                    <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
                      {comic.title}
                    </h1>

                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      {comic.status === 'ongoing' ? (
                        <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">连载中</span>
                      ) : (
                        <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">已完结</span>
                      )}
                      <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                      <span className="text-sm text-foreground-500">共 {comic.chapters} 话</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-5">
                      {comic.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-4 mb-5 text-sm">
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-yellow-500"></i>
                        <span className="text-foreground-700 font-semibold">{comic.rating}</span>
                        <span className="text-foreground-400">分</span>
                      </div>
                      <div className="flex items-center gap-1 text-foreground-400">
                        <i className="ri-eye-line"></i>
                        <span>{(comic.views / 10000).toFixed(0)}万 阅读</span>
                      </div>
                      <div className="flex items-center gap-1 text-foreground-400">
                        <i className="ri-calendar-line"></i>
                        <span>更新于 {comic.updateDate}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 mb-6">
                      <button className="inline-flex items-center px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                        <i className="ri-play-circle-line mr-1.5"></i>
                        开始阅读
                      </button>
                      <button className="inline-flex items-center px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                        <i className="ri-bookmark-line mr-1.5"></i>
                        收藏
                      </button>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">作品简介</h2>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {comic.description}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading text-lg font-bold text-foreground-900">章节列表</h2>
                      <button className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                        <i className="ri-sort-desc mr-1"></i>倒序
                      </button>
                    </div>

                    <div className="border border-background-200 rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
                      <div className="divide-y divide-background-100">
                        {comic.chapters_list.map((ch) => (
                          <Link
                            key={ch.id}
                            to={`/comic/${comic.slug}/chapter/${ch.id}`}
                            className="flex items-center justify-between px-4 py-3 hover:bg-background-50 transition-colors cursor-pointer"
                          >
                            <span className="text-sm text-foreground-700">{ch.title}</span>
                            <span className="text-xs text-foreground-400">{ch.date}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                {relatedComics.map((rc, i) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3 aspect-[7/10] bg-background-200">
                      <img
                        src={relatedCoverImages[i] || relatedCoverImages[0]}
                        alt={rc.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-1">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}